import React, { useRef, useState } from 'react';
import { HelpTooltip } from './HelpTooltip';

export interface MultiImageValue {
  dataUrl: string;
  filename: string;
  contentType: string;
  size: number;
}

interface MultiImageUploadWidgetProps {
  label: string;
  value?: MultiImageValue[];
  onChange: (value: MultiImageValue[]) => void;
  error?: string;
  disabled?: boolean;
  description?: string;
  maxSizeMB?: number;
  maxFiles?: number;
  helpText?: string;
}

const readAsDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = e => resolve(e.target?.result as string);
  reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
  reader.readAsDataURL(file);
});

export const MultiImageUploadWidget: React.FC<MultiImageUploadWidgetProps> = ({
  label, value = [], onChange, error, disabled = false, description,
  maxSizeMB = 10, maxFiles = 12, helpText
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failedPreviews, setFailedPreviews] = useState<Set<number>>(new Set());

  const addFiles = async (fileList: FileList | File[]) => {
    if (disabled) return;
    const files = Array.from(fileList).filter(file => {
      const name = file.name.toLowerCase();
      return file.type.startsWith('image/') || name.endsWith('.heic') || name.endsWith('.heif');
    });
    if (files.length === 0) return;
    const remaining = Math.max(0, maxFiles - value.length);
    const accepted = files.slice(0, remaining);
    const tooLarge = accepted.find(file => file.size / (1024 * 1024) > maxSizeMB);
    if (tooLarge) {
      alert(`${tooLarge.name} is too large. Maximum size is ${maxSizeMB}MB.`);
      return;
    }
    if (accepted.length < files.length) {
      alert(`Only ${remaining} more image(s) can be added. Maximum is ${maxFiles}.`);
    }
    setLoading(true);
    try {
      const uploads = await Promise.all(accepted.map(async file => ({
        dataUrl: await readAsDataUrl(file),
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      })));
      onChange([...value, ...uploads]);
      setFailedPreviews(new Set());
    } catch (err: any) {
      alert(err.message || 'Failed to read image files');
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
    setFailedPreviews(new Set());
  };

  return (
    <div className="mb-4">
      <div className="widget-label-row mb-2">
        <label className="block text-sm font-medium text-nordic-gray-700">{label}</label>
        {helpText && <HelpTooltip helpText={helpText} />}
      </div>
      {description && <p className="text-sm text-nordic-gray-500 mb-2">{description}</p>}

      <div
        className={`relative border-2 border-dashed rounded-lg transition-colors ${dragActive ? 'border-nordic-blue-500 bg-nordic-blue-50' : error ? 'border-red-300 bg-red-50' : 'border-nordic-gray-300 bg-white hover:border-nordic-gray-400'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragEnter={e => { e.preventDefault(); setDragActive(true); }}
        onDragOver={e => e.preventDefault()}
        onDragLeave={e => { e.preventDefault(); setDragActive(false); }}
        onDrop={e => { e.preventDefault(); setDragActive(false); addFiles(e.dataTransfer.files); }}
      >
        <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple disabled={disabled}
          onChange={e => e.target.files && addFiles(e.target.files)} className="hidden" />
        <div className="p-4">
          {value.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {value.map((item, index) => (
                <div key={`${item.filename}-${index}`} className="relative aspect-square rounded-md overflow-hidden bg-gray-100 border border-gray-200">
                  {failedPreviews.has(index) ? (
                    <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                      <span className="text-xs font-medium text-nordic-gray-700 truncate max-w-full">{item.filename}</span>
                      <span className="text-[10px] text-nordic-gray-500 mt-1">Will convert in ComfyUI</span>
                    </div>
                  ) : (
                    <img
                      src={item.dataUrl}
                      alt={item.filename || `Upload ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={() => setFailedPreviews(prev => new Set(prev).add(index))}
                    />
                  )}
                  {!disabled && <button type="button" onClick={e => { e.stopPropagation(); removeAt(index); }} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-red-500 text-white shadow-md flex items-center justify-center" title="Remove image">×</button>}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <svg className="w-12 h-12 text-nordic-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
              <p className="text-sm font-medium text-nordic-gray-700 mb-1">Select multiple images</p>
              <p className="text-xs text-nordic-gray-500">HEIC, PNG, JPG, GIF, WebP up to {maxSizeMB}MB each</p>
            </div>
          )}
          {value.length > 0 && <p className="mt-3 text-xs text-center text-nordic-gray-500">{loading ? 'Reading images…' : `${value.length}/${maxFiles} selected · tap to add more`}</p>}
        </div>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};